import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { commitActivity } from '@mkcore/dev/schema';
import { Badge, Card, EmptyState, ErrorState, Field, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, cn } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';

const search = z.object({ days: z.coerce.number().pipe(z.union([z.literal(7), z.literal(30), z.literal(90)])).optional(), who: z.enum(['all', 'humans', 'bots']).optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/dev/activity')({ validateSearch: search, component: Activity });

/** Five steps of colour, from none to the busiest day of the window. */
const shade = (n: number, max: number) => (n === 0 ? 'bg-sunken' : n / max > 0.75 ? 'bg-gold-strong' : n / max > 0.5 ? 'bg-gold' : n / max > 0.25 ? 'bg-gold/60' : 'bg-gold/30');

/**
 * Commit activity (CO-0417): commits on the default branches per person per day, one person behind several logins
 * and addresses through the identity aliases, bots marked; and activity per repository. Days in Europe/Amsterdam.
 */
function Activity() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const days = s.days ?? 30;
  const q = useQuery({ queryKey: ['dev', 'activity', days, s.who ?? 'all'], queryFn: () => call(commitActivity, { days, who: s.who ?? 'all' }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  const max = Math.max(1, ...(q.data?.people.flatMap((p) => p.perDay.map((d) => d.n)) ?? [1]));
  return (
    <>
      <PageHeader eyebrow={t('nav.dev')} title={t('activity.title')} description={t('activity.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('activity.window')} className="w-36">
          {(p) => (
            <NativeSelect {...p} value={String(days)} onChange={(e) => set({ days: Number(e.target.value) as 7 | 30 | 90 })}>
              {[7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {t('activity.days', { count: d })}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('activity.who')} className="w-36">
          {(p) => (
            <NativeSelect {...p} value={s.who ?? 'all'} onChange={(e) => set({ who: e.target.value as Search['who'] })}>
              <option value="all">{t('activity.whoAll')}</option>
              <option value="humans">{t('activity.whoHumans')}</option>
              <option value="bots">{t('activity.whoBots')}</option>
            </NativeSelect>
          )}
        </Field>
      </div>
      {q.data?.uncertain && (
        <p className="mb-3 text-sm text-warning" role="status">
          {t('activity.uncertain')}
        </p>
      )}
      {!q.data ? (
        <Skeleton className="h-64" />
      ) : !q.data.people.length ? (
        <EmptyState title={t('activity.empty')} />
      ) : (
        <Card className="mb-6 overflow-x-auto p-4">
          <table className="border-separate border-spacing-0.5 text-xs" aria-label={t('activity.heatmap')}>
            <thead>
              <tr>
                <th className="pr-2 text-left font-normal text-fg-3">{t('activity.person')}</th>
                {q.data.days.map((d, n) => (
                  <th key={d} className="w-3.5 font-normal text-fg-3" scope="col">
                    <span className="sr-only">{formatDate(d, 'UTC')}</span>
                    <span aria-hidden>{n % 7 === 0 ? d.slice(8) : ''}</span>
                  </th>
                ))}
                <th className="pl-2 text-right font-normal text-fg-3">{t('activity.total')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.people.map((p) => {
                const byDay = new Map(p.perDay.map((d) => [d.day, d.n]));
                return (
                  <tr key={p.key}>
                    <th scope="row" className="max-w-48 truncate pr-2 text-left font-normal">
                      {p.key}
                      {p.bot && (
                        <Badge tone="outline" className="ml-1">
                          {t('activity.bot')}
                        </Badge>
                      )}
                    </th>
                    {q.data.days.map((d) => {
                      const n = byDay.get(d) ?? 0;
                      return (
                        <td key={d} className={cn('size-3.5 rounded-sm', shade(n, max))} title={t('activity.cell', { name: p.key, day: formatDate(d, 'UTC'), count: n })}>
                          <span className="sr-only">{n}</span>
                        </td>
                      );
                    })}
                    <td className="pl-2 text-right tabular">{p.total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
      {q.data && q.data.repositories.length > 0 && (
        <>
          <h2 className="mb-2 text-lg font-bold">{t('activity.repositories')}</h2>
          <Table>
            <THead>
              <Tr>
                <Th>{t('issues.repository')}</Th>
                <Th className="text-right">{t('activity.commits')}</Th>
                <Th className="text-right">{t('activity.people')}</Th>
                <Th className="hidden md:table-cell">{t('activity.lastCommit')}</Th>
              </Tr>
            </THead>
            <TBody>
              {q.data.repositories.map((r) => (
                <Tr key={r.repositoryId}>
                  <Td className="font-bold">
                    {r.fullName}
                    {r.uncertain && (
                      <Badge tone="warning" className="ml-2">
                        {t('compliance.uncertain')}
                      </Badge>
                    )}
                  </Td>
                  <Td className="text-right tabular">{r.commits}</Td>
                  <Td className="text-right tabular">{r.people}</Td>
                  <Td className="hidden md:table-cell tabular text-fg-2">{r.lastCommitAt ? formatDateTime(r.lastCommitAt) : '—'}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </>
      )}
    </>
  );
}
